'use client';

import { useMemo, useState } from 'react';
import Link from 'next/link';
import { motion } from 'framer-motion';
import { PortableText } from '@portabletext/react';
import { stegaClean } from '@sanity/client/stega';

import { resolveLink } from '@/sanity/globals/CTA';
import { resolveI18nText } from '@/sanity/globals/I18n';
import { ICareerRoleCard } from '@/sanity/lib/definitions';
import { PageCareerOverviewSchema } from '@/sanity/pages/PageCareerOverviewTemplate';
import { richTextComponent } from '@/sanity/sanityPortableText';

const ease = [0.25, 0.1, 0.25, 1] as const;

function ArrowIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" className="h-[2rem] w-[2rem]" aria-hidden="true">
      <path
        d="M3 8h10M9 4l4 4-4 4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function JobRow({
  role,
  language,
  active,
  onHover,
}: {
  role: ICareerRoleCard;
  language: string;
  active: boolean;
  onHover: () => void;
}) {
  const slug = stegaClean(role.slug) ?? '';
  const href = slug ? resolveLink(slug, language) : '#';
  const meta = (stegaClean(role.listMeta) ?? []).filter(Boolean);

  return (
    <Link
      href={href}
      onMouseEnter={onHover}
      onFocus={onHover}
      className={`group flex min-h-[12.5rem] w-full items-center justify-between px-[3rem] transition-colors duration-300 max-lg:gap-[0.5rem] max-lg:py-[3.125rem] lg:min-h-[15.5rem] lg:gap-[2rem] lg:px-[4.5rem] lg:py-[4rem] ${
        active ? 'ring-1 ring-[#228e71] max-lg:bg-[#ecf6f4] lg:bg-[#e2f3ef]' : 'bg-[#f4f5f5]'
      }`}
    >
      <span className="flex flex-col max-lg:gap-[1.5rem] lg:gap-[1.625rem]">
        <span className="text-[2.5rem] font-medium tracking-[-0.08rem] text-black max-lg:leading-[1.35] lg:leading-[1.25]">
          {role.listTitle}
        </span>
        {meta.length > 0 && (
          <span className="flex flex-wrap items-center gap-y-[1.5rem] font-geist-mono text-[1.5rem] uppercase leading-[1.4] text-black/50 max-lg:gap-x-[2.25rem] lg:gap-x-[1.8rem]">
            {meta.map((m, i) => (
              <span key={m} className="flex items-center max-lg:gap-x-[2.25rem] lg:gap-x-[1.8rem]">
                {i > 0 && <span className="text-[#228e71]">•</span>}
                {m}
              </span>
            ))}
          </span>
        )}
      </span>

      <span
        className={`flex h-[5rem] w-[5rem] shrink-0 items-center justify-center border transition-colors duration-300 lg:h-[4.5rem] lg:w-[4.5rem] ${
          active
            ? 'text-[#0b6f53] max-lg:border-[#92ada6] lg:border-[#228e71]'
            : 'border-[rgba(0,0,0,0.15)] text-black/40 max-lg:border-black/10'
        }`}
      >
        <ArrowIcon />
      </span>
    </Link>
  );
}

const ALL_LABEL: Record<string, string> = {
  en: 'All',
  de: 'Alle',
  br: 'Todas',
};
const EMPTY_LABEL: Record<string, string> = {
  en: 'No open positions in this category right now.',
  de: 'Aktuell keine offenen Stellen in dieser Kategorie.',
  br: 'Nenhuma vaga aberta nesta categoria no momento.',
};

export default function JobListSection({ data }: { data: PageCareerOverviewSchema }) {
  const language = stegaClean(data.settings?.language) ?? 'en';
  const roles = data.roles ?? [];
  const categories = data.categories ?? [];
  const allLabel = stegaClean(data.jobsAllLabel) || ALL_LABEL[language] || ALL_LABEL.en;

  const [selected, setSelected] = useState<string | null>(null);
  const [hovered, setHovered] = useState<string | null>(null);

  const usedCategories = useMemo(() => {
    const ids = new Set(roles.map((r) => r.category?._id).filter(Boolean));
    return categories.filter((c) => ids.has(c._id));
  }, [categories, roles]);

  const visible = useMemo(
    () => (selected ? roles.filter((r) => r.category?._id === selected) : roles),
    [roles, selected],
  );

  const activeId = hovered ?? visible[0]?._id ?? null;

  if (!data.jobsTitle && roles.length === 0) return null;

  return (
    <section id="jobs" className="w-full bg-white px-[2rem] py-[8rem] lg:px-[6rem] lg:py-[15rem]">
      <div className="mx-auto flex w-[min(168.5rem,100%)] flex-col items-center">
        {data.jobsChip && (
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.8, ease }}
            className="mb-0 text-center font-blacker text-[2rem] italic leading-[1.5] text-black"
          >
            {data.jobsChip}
          </motion.p>
        )}

        {data.jobsTitle && (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.8, ease, delay: 0.1 }}
            className="richtext mt-[1rem] w-full prose-headings:mb-0 prose-headings:text-[4rem] prose-headings:font-normal prose-headings:leading-[1.1] lg:mt-[2.5rem] lg:prose-headings:text-[6rem] lg:prose-headings:tracking-[-0.24rem]"
          >
            <PortableText value={data.jobsTitle} components={richTextComponent} />
          </motion.div>
        )}

        <div className="flex w-full flex-col gap-[4rem] max-lg:mt-[3.875rem] lg:mt-[8rem] lg:flex-row lg:gap-[6rem]">
          {/* Categories */}
          {usedCategories.length > 0 && (
            <nav
              className="flex shrink-0 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden max-lg:-mx-[2rem] max-lg:w-[calc(100%+4rem)] max-lg:gap-[2rem] max-lg:pl-[2rem] max-lg:after:w-0 max-lg:after:shrink-0 max-lg:after:content-[''] lg:w-[34rem] lg:flex-col lg:gap-0 lg:overflow-x-visible"
              aria-label={allLabel}
            >
              <button
                type="button"
                onClick={() => {
                  setSelected(null);
                  setHovered(null);
                }}
                className={`shrink-0 border-b font-geist transition-colors max-lg:min-w-[10.5625rem] max-lg:whitespace-nowrap max-lg:pb-[1.25rem] max-lg:pl-[0.5rem] max-lg:pr-0 max-lg:pt-0 max-lg:text-center max-lg:text-[2.25rem] max-lg:leading-[1.5] max-lg:tracking-[-0.05em] lg:w-full lg:whitespace-normal lg:px-0 lg:py-[1.75rem] lg:text-left lg:text-[2rem] lg:leading-[1.4] ${
                  selected === null
                    ? 'font-semibold max-lg:border-b-2 max-lg:border-[rgba(0,0,0,0.15)] max-lg:text-[#606060] lg:border-black/10 lg:text-black'
                    : 'border-black/10 hover:text-black max-lg:text-black/20 lg:text-black/[0.45]'
                }`}
              >
                {allLabel}
              </button>
              {usedCategories.map((category) => {
                const label = resolveI18nText(category.name, category.nameI18n, language);
                const isOn = selected === category._id;
                return (
                  <button
                    key={category._id}
                    type="button"
                    onClick={() => {
                      setSelected(category._id ?? null);
                      setHovered(null);
                    }}
                    className={`shrink-0 border-b font-geist transition-colors max-lg:whitespace-nowrap max-lg:pb-[1.25rem] max-lg:pl-[0.5rem] max-lg:pr-0 max-lg:pt-0 max-lg:text-center max-lg:text-[2.25rem] max-lg:leading-[1.5] max-lg:tracking-[-0.05em] lg:w-full lg:whitespace-normal lg:px-0 lg:py-[1.75rem] lg:text-left lg:text-[2rem] lg:leading-[1.4] ${
                      isOn
                        ? 'font-semibold max-lg:border-b-2 max-lg:border-[rgba(0,0,0,0.15)] max-lg:text-[#606060] lg:border-black/10 lg:text-black'
                        : 'border-black/10 hover:text-black max-lg:text-black/20 lg:text-black/[0.45]'
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </nav>
          )}

          <div key={selected ?? 'all'} className="flex w-full flex-col gap-[1.5rem]">
            {visible.map((role, index) => (
              <motion.div
                key={role._id ?? index}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, ease, delay: Math.min(index * 0.05, 0.2) }}
              >
                <JobRow
                  role={role}
                  language={language}
                  active={role._id === activeId}
                  onHover={() => setHovered(role._id ?? null)}
                />
              </motion.div>
            ))}

            {visible.length === 0 && (
              <p className="mb-0 py-[4rem] text-center text-[2rem] text-black/50">
                {stegaClean(data.jobsEmptyLabel) ||
                  EMPTY_LABEL[language] ||
                  EMPTY_LABEL.en}
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
